import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useT } from '../context/LanguageContext';
import { API_BASE as BASE } from '../lib/apiBase';
import { FEATURES, BetaalInstructies } from './PricingPage';
import PlanComparison from '../components/PlanComparison';

// In-app plannen-overzicht. Gebruikt exact dezelfde bron (/api/webshop/packages),
// features en betaalflow als de publieke /pricing pagina, zodat ze altijd gelijk zijn.
export default function Pricing({ currentPlan = 'NONE', defaultEmail = '', onActivate }) {
  const { t } = useT();
  const [packages, setPackages] = useState({});
  const [busy, setBusy] = useState('');
  const [emails, setEmails] = useState({});
  const [order, setOrder] = useState(null);

  useEffect(() => {
    axios.get(`${BASE}/api/webshop/packages`).then(r => setPackages(r.data)).catch(() => {});
  }, []);

  const bestel = async (pkgId) => {
    const email = (emails[pkgId] ?? defaultEmail ?? '').trim();
    if (!email || !email.includes('@')) { toast.error(t('Vul je e-mailadres in', 'Vul je e-mailadres in')); return; }
    setBusy(pkgId);
    try {
      const r = await axios.post(`${BASE}/api/webshop/order`, { package_id: pkgId, email });
      setOrder(r.data);
    } catch (e) {
      toast.error(t('Bestellen mislukt', 'Bestellen mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setBusy(''); }
  };

  return (
    <div data-testid="pricing-page">
      <div style={{ textAlign: 'center', padding: '4px 0 18px' }}>
        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>{t('Kies het plan dat bij jouw garage past', 'Kies het plan dat bij jouw garage past')}</h3>
        <p style={{ color: 'var(--cs-text-muted)', marginTop: 6, fontSize: 13.5 }}>
          {t('Betalen via bankoverschrijving of PayPal · Licentiekey automatisch per e-mail na ontvangst van je betaling', 'Betalen via bankoverschrijving of PayPal · Licentiekey automatisch per e-mail na ontvangst van je betaling')}
        </p>
      </div>

      <div style={{ marginBottom: 24 }}>
        <PlanComparison />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(248px, 1fr))', gap: 16 }}>
        {(() => {
          const basicCurrent = currentPlan === 'NONE' || currentPlan === 'BASIC' || !currentPlan;
          return (
            <div data-testid="pricing-card-basic" style={{
              background: '#fff', borderRadius: 12, padding: 22,
              border: '2px solid #7c3aed',
              boxShadow: basicCurrent ? '0 10px 30px rgba(124,58,237,.16)' : '0 2px 6px rgba(15,23,42,.04)',
              position: 'relative', display: 'flex', flexDirection: 'column',
            }}>
              {basicCurrent && <div style={{ position: 'absolute', top: -12, left: 16, background: '#7c3aed', color: '#fff', padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700 }} data-testid="current-badge-basic">{t('Jouw plan', 'Jouw plan')}</div>}
              <h3 style={{ margin: '0 0 4px', fontSize: 19, color: '#0f172a' }}>{t('Gratis', 'Gratis')}</h3>
              <div style={{ fontSize: 11, color: '#7c3aed', textTransform: 'uppercase', marginBottom: 14, fontWeight: 700 }}>BASIC</div>
              <div style={{ fontSize: 34, fontWeight: 800, color: '#7c3aed', marginBottom: 4 }}>€0</div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>{t('Altijd gratis · voor zzp & starters', 'Altijd gratis · voor zzp & starters')} · 1 seat</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 18px', fontSize: 13, color: '#334155', flex: 1 }}>
                {(FEATURES.BASIC || []).map(f => (
                  <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '4px 0' }}>
                    <Check size={14} color="#7c3aed" style={{ flexShrink: 0, marginTop: 2 }} /> {t(f, f)}
                  </li>
                ))}
              </ul>
              <button disabled data-testid="pricing-basic-current" style={{
                width: '100%', padding: '12px', border: '1px solid #ddd6fe', borderRadius: 8,
                background: '#f5f3ff', color: '#7c3aed', fontSize: 14, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'default',
              }}>
                <Sparkles size={14} /> {basicCurrent ? t('Jouw huidige plan', 'Jouw huidige plan') : t('Gratis instapplan', 'Gratis instapplan')}
              </button>
            </div>
          );
        })()}
        {Object.entries(packages).map(([id, p]) => {
          const isPopular = id === 'pro_yearly';
          const isCurrent = p.plan === currentPlan;
          const duurText = p.days === 0
            ? t('eenmalig, voor altijd', 'eenmalig, voor altijd')
            : p.days >= 365
              ? `${t('eenmalig', 'eenmalig')}, ${Math.round(p.days / 365)} ${t('jaar geldig', 'jaar geldig')}`
              : `${t('eenmalig', 'eenmalig')}, ${p.days} ${t('dagen geldig', 'dagen geldig')}`;
          return (
            <div key={id} data-testid={`pricing-card-${id}`} style={{
              background: '#fff', borderRadius: 12, padding: 22,
              border: isPopular ? '2px solid #2563eb' : '1px solid #e2e8f0',
              boxShadow: isPopular ? '0 10px 30px rgba(37,99,235,.16)' : '0 2px 6px rgba(15,23,42,.04)',
              position: 'relative', display: 'flex', flexDirection: 'column',
            }}>
              {isPopular && <div style={{ position: 'absolute', top: -12, right: 16, background: '#2563eb', color: '#fff', padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{t('POPULAIR', 'POPULAIR')}</div>}
              {isCurrent && <div style={{ position: 'absolute', top: -12, left: 16, background: '#10b981', color: '#fff', padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700 }} data-testid={`current-badge-${id}`}>{t('Jouw plan', 'Jouw plan')}</div>}
              <h3 style={{ margin: '0 0 4px', fontSize: 19, color: '#0f172a' }}>{t(p.label, p.label)}</h3>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', marginBottom: 14 }}>{p.plan}</div>
              <div style={{ fontSize: 34, fontWeight: 800, color: isPopular ? '#2563eb' : '#0f172a', marginBottom: 4 }}>
                €{p.amount.toFixed(0)}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
                {duurText} · {p.seats} {p.seats > 1 ? t('gebruikers', 'gebruikers') : t('gebruiker', 'gebruiker')}
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 18px', fontSize: 13, color: '#334155', flex: 1 }}>
                {(FEATURES[p.plan] || []).map(f => (
                  <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '4px 0' }}>
                    <Check size={14} color="#10b981" style={{ flexShrink: 0, marginTop: 2 }} /> {t(f, f)}
                  </li>
                ))}
              </ul>
              <input
                type="email"
                placeholder="jouw@email.nl"
                value={emails[id] ?? defaultEmail}
                onChange={e => setEmails({ ...emails, [id]: e.target.value })}
                data-testid={`pricing-email-${id}`}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, marginBottom: 10, boxSizing: 'border-box' }}
              />
              <button
                data-testid={`pricing-buy-${id}`}
                disabled={busy === id}
                onClick={() => bestel(id)}
                style={{
                  width: '100%', padding: '12px', border: 'none', borderRadius: 8,
                  background: isPopular ? '#2563eb' : '#0f172a', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                {busy === id ? <Loader2 size={14} className="spin" /> : null}
                {busy === id ? t('Bezig...', 'Bezig...') : `${t('Bestel', 'Bestel')} ${t(p.label,p.label)}`}
              </button>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 18, padding: 14, background: 'var(--cs-card-alt)', borderRadius: 8, fontSize: 12.5, color: 'var(--cs-text-muted)' }}>
        <b>{t('Hoe werkt activatie?', 'Hoe werkt activatie?')}</b> {t('Je ontvangt na betaling een cryptografisch ondertekende licentiekey via e-mail.', 'Je ontvangt na betaling een cryptografisch ondertekende licentiekey via e-mail.')}
        {' '}{t('Plak deze in het Licentie tabblad om je plan direct te activeren. De licentie wordt gebonden aan jouw garage en kan niet hergebruikt worden door anderen.', 'Plak deze in het Licentie tabblad om je plan direct te activeren. De licentie wordt gebonden aan jouw garage en kan niet hergebruikt worden door anderen.')}
        {onActivate && (
          <div style={{ marginTop: 10 }}>
            <button className="cs-btn" data-testid="goto-licentie" onClick={onActivate}>{t('Ik heb al een licentiekey →', 'Ik heb al een licentiekey →')}</button>
          </div>
        )}
      </div>

      {order && <BetaalInstructies order={order} onClose={() => setOrder(null)} t={t} />}
      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}
